import { useCallback, useEffect, useMemo, useState } from 'react'
import * as XLSX from 'xlsx'
import Modal from 'react-bootstrap/Modal'
import Button from 'react-bootstrap/Button'
import Form from 'react-bootstrap/Form'
import Table from 'react-bootstrap/Table'
import Alert from 'react-bootstrap/Alert'
import { bulkCreateEventReserved } from '../../utilities/api'
import {
  parseWorkbookToRows,
  validateDataRow,
  rowsToBulkApiPayload,
  partitionRowsForBulkUpload,
  consolidateBulkUploadRowsByEmail,
  formatBulkRowSeatSummary,
  mergeBulkImportResults,
  MAX_BULK_RESERVED_ROWS,
} from './bulkReservedImportShared'
import { startBulkReservedImportPoll } from './bulkReservedImportBackground'
import { downloadBulkReservedImportErrorCsv } from './bulkReservedImportErrorCsv'

const PREVIEW_LIMIT = 15

export default function AssignTicketsBulkModal({
  eventUUID,
  show,
  onHide,
  disabled,
  onCompleted,
}) {
  const [fileLabel, setFileLabel] = useState('')
  const [parsedRows, setParsedRows] = useState([])
  const [parseError, setParseError] = useState('')
  const [submitting, setSubmitting] = useState(false)
  const [submitElapsedSec, setSubmitElapsedSec] = useState(0)
  const [submitError, setSubmitError] = useState('')
  const [lastResult, setLastResult] = useState(null)
  const [queuedNotice, setQueuedNotice] = useState(false)

  useEffect(() => {
    if (!submitting) {
      setSubmitElapsedSec(0)
      return undefined
    }
    const started = Date.now()
    const id = setInterval(() => {
      setSubmitElapsedSec(Math.floor((Date.now() - started) / 1000))
    }, 1000)
    return () => clearInterval(id)
  }, [submitting])

  const resetState = useCallback(() => {
    setFileLabel('')
    setParsedRows([])
    setParseError('')
    setSubmitting(false)
    setSubmitError('')
    setLastResult(null)
    setQueuedNotice(false)
  }, [])

  const handleClose = () => {
    resetState()
    onHide()
  }

  const { uploadRows, skippedRows } = useMemo(
    () => partitionRowsForBulkUpload(parsedRows),
    [parsedRows]
  )

  const consolidatedRows = useMemo(
    () => consolidateBulkUploadRowsByEmail(uploadRows),
    [uploadRows]
  )

  const uploadRowErrors = useMemo(
    () =>
      consolidatedRows.map((row) => ({
        excelRow: row.excelRow,
        errors: validateDataRow(row),
      })),
    [consolidatedRows]
  )

  const canSubmit = useMemo(() => {
    if (!consolidatedRows.length || parseError) return false
    if (uploadRowErrors.some((e) => e.errors.length)) return false
    return true
  }, [consolidatedRows, parseError, uploadRowErrors])

  const onFile = (e) => {
    const file = e.target.files?.[0]
    setParseError('')
    setSubmitError('')
    setLastResult(null)
    setQueuedNotice(false)
    setParsedRows([])
    if (!file) {
      setFileLabel('')
      return
    }
    setFileLabel(file.name)
    const reader = new FileReader()
    reader.onload = (ev) => {
      try {
        const data = new Uint8Array(ev.target.result)
        const workbook = XLSX.read(data, { type: 'array' })
        const out = parseWorkbookToRows(workbook, XLSX)
        if (out.error) {
          setParseError(out.error)
          return
        }
        setParsedRows(out.rows)
      } catch (err) {
        console.error(err)
        setParseError('Could not read this Excel file.')
      }
    }
    reader.readAsArrayBuffer(file)
  }

  const handleSubmit = async (sendConfirmationEmail) => {
    if (!canSubmit || !eventUUID) return

    setSubmitting(true)
    setSubmitError('')
    setLastResult(null)
    setQueuedNotice(false)

    const payloadRows = rowsToBulkApiPayload(consolidatedRows)

    const formatAxiosError = (err) =>
      String(
        err?.response?.data?.error?.message ||
          err?.response?.data?.message ||
          err?.message ||
          'Bulk import failed.'
      )

    try {
      const res = await bulkCreateEventReserved({
        eventUUID,
        rows: payloadRows,
        sendConfirmationEmail,
      })

      if (res.status === 202) {
        const batchId = res.data?.batchId
        setQueuedNotice(true)
        startBulkReservedImportPoll(batchId, {
          skippedRows,
          onCompleted: () => {
            if (onCompleted) onCompleted()
          },
        })
      } else {
        setLastResult({
          results: mergeBulkImportResults(res.data?.results || [], skippedRows),
          batchId: res.data?.batchId ?? null,
          batchIds: res.data?.batchId ? [res.data.batchId] : [],
          batchesProcessed: 1,
          batchesTotal: 1,
          partial: false,
        })
      }

      if (res.status !== 202 && onCompleted) {
        onCompleted()
      }
    } catch (err) {
      setSubmitError(formatAxiosError(err))
    } finally {
      setSubmitting(false)
    }
  }

  const failedApiRows = lastResult?.results?.filter((r) => !r.ok) || []

  return (
    <Modal show={show} onHide={handleClose} size="xl" centered>
      <Modal.Header closeButton>
        <Modal.Title>Bulk upload reserved ticket orders</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        <p className="text-muted small mb-3">
          Required Excel columns:{' '}
          <code>first_name, last_name, email, section, row, seats</code>.
          Optional:{' '}
          <code>
            organization, phone_numbers, buyer_type, price, ga, quantity
          </code>
          . Rows with <code>GA</code>, <code>GA5</code>, <code>GA 5</code>, etc.
          in the row column count as GA without <code>ga=true</code>. Otherwise
          set <code>ga</code> to <code>true</code> with a <code>quantity</code>{' '}
          column or numeric / comma placeholders in <code>seats</code>. Else
          seats must match inventory (comma-separated). Rows without an email
          are skipped and listed in the error report. Rows with the same email
          are combined into one reserved order. At most {MAX_BULK_RESERVED_ROWS}{' '}
          data rows per file. After you submit, the server processes rows in the
          background; you can close this dialog and a toast will appear when the
          run finishes.
        </p>
        <Form.Group className="mb-3">
          <Form.Label>Excel file (.xlsx)</Form.Label>
          <Form.Control
            type="file"
            accept=".xlsx,.xls"
            onChange={onFile}
            disabled={disabled || submitting}
          />
          {fileLabel ? (
            <Form.Text muted>Selected: {fileLabel}</Form.Text>
          ) : null}
        </Form.Group>

        {parseError ? <Alert variant="danger">{parseError}</Alert> : null}
        {queuedNotice ? (
          <Alert variant="success" className="py-2">
            Upload received. Processing {consolidatedRows.length} order(s) in
            the background — you can close this window. A notification will
            appear when the import completes.
            {skippedRows.length ? (
              <span className="d-block mt-1">
                {skippedRows.length} row(s) without email were skipped.
              </span>
            ) : null}
          </Alert>
        ) : null}

        {submitting ? (
          <Alert variant="info" className="py-2">
            Uploading… Elapsed {submitElapsedSec}s.
          </Alert>
        ) : null}

        {submitError ? <Alert variant="danger">{submitError}</Alert> : null}

        {parsedRows.length > 0 && !parseError ? (
          <>
            <p className="mb-2">
              <strong>{parsedRows.length}</strong> seat row(s) loaded →{' '}
              <strong>{consolidatedRows.length}</strong> reserved order(s)
              {skippedRows.length ? (
                <> ({skippedRows.length} skipped — missing email)</>
              ) : null}
              .
            </p>
            {parsedRows.length > 0 && !consolidatedRows.length ? (
              <Alert variant="danger" className="py-2">
                Every loaded row is missing an email. Add emails or remove those
                rows to continue.
              </Alert>
            ) : null}
            {skippedRows.length ? (
              <Alert variant="info" className="py-2">
                {skippedRows.length} row(s) without an email will be skipped and
                included in the error report after upload.
              </Alert>
            ) : null}
            {uploadRowErrors.some((e) => e.errors.length) ? (
              <Alert variant="warning">
                Fix validation errors before submitting:
                <ul className="mb-0 mt-2">
                  {uploadRowErrors
                    .filter((e) => e.errors.length)
                    .slice(0, 20)
                    .map((e) => (
                      <li key={e.excelRow}>
                        Row {e.excelRow}: {e.errors.join('; ')}
                      </li>
                    ))}
                </ul>
              </Alert>
            ) : null}

            <div className="table-responsive mb-3" style={{ maxHeight: 280 }}>
              <Table
                size="sm"
                striped
                bordered
                hover
                style={{ tableLayout: 'fixed', width: '100%' }}
              >
                <colgroup>
                  <col style={{ width: '3.5rem' }} />
                  <col style={{ width: '32%' }} />
                  <col style={{ width: '26%' }} />
                  <col />
                </colgroup>
                <thead>
                  <tr>
                    <th>#</th>
                    <th>Email</th>
                    <th>Name</th>
                    <th>Seats</th>
                  </tr>
                </thead>
                <tbody>
                  {consolidatedRows.slice(0, PREVIEW_LIMIT).map((r) => (
                    <tr key={`${r.excelRow}-${r.email}`}>
                      <td className="align-top text-muted text-nowrap">
                        {r.excelRow}
                      </td>
                      <td className="align-top text-wrap text-break">
                        {String(r.email)}
                      </td>
                      <td className="align-top text-wrap text-break">
                        {String(r.first_name)} {String(r.last_name)}
                      </td>
                      <td className="align-top text-wrap text-break">
                        {formatBulkRowSeatSummary(r)}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </Table>
            </div>
            {consolidatedRows.length > PREVIEW_LIMIT ? (
              <p className="text-muted small">
                Showing first {PREVIEW_LIMIT} orders of{' '}
                {consolidatedRows.length}.
              </p>
            ) : null}
          </>
        ) : null}

        {lastResult?.results?.length ? (
          <Alert
            variant={failedApiRows.length ? 'warning' : 'success'}
            className="mt-2"
          >
            Created{' '}
            <strong>{lastResult.results.filter((r) => r.ok).length}</strong> of{' '}
            <strong>{lastResult.results.length}</strong> orders.
            {lastResult.partial ? (
              <span className="d-block small mt-1 text-muted">
                Import stopped early because of an error.
              </span>
            ) : null}
            {failedApiRows.length ? (
              <>
                {' '}
                <strong>{failedApiRows.length}</strong> failed.
                <div className="mt-2">
                  <Button
                    size="sm"
                    variant="outline-dark"
                    onClick={() =>
                      downloadBulkReservedImportErrorCsv(
                        (lastResult.batchIds || []).join('_').slice(0, 80) ||
                          lastResult.batchId,
                        failedApiRows,
                        'event_bulk_reserved_errors'
                      )
                    }
                  >
                    Download error report (CSV)
                  </Button>
                </div>
              </>
            ) : null}
          </Alert>
        ) : null}
      </Modal.Body>
      <Modal.Footer className="d-flex flex-wrap gap-2 justify-content-between">
        <Button variant="outline-light" onClick={handleClose}>
          Close
        </Button>
        <div className="d-flex flex-wrap gap-2">
          {/* <Button
            variant="primary"
            disabled={!canSubmit || disabled || submitting}
            onClick={() => handleSubmit(true)}
          >
            {submitting ? "Working…" : "Create reserved orders + email"}
          </Button> */}
          <Button
            variant="primary"
            disabled={!canSubmit || disabled || submitting}
            onClick={() => handleSubmit(false)}
          >
            {submitting ? 'Working…' : 'Create'}
          </Button>
        </div>
      </Modal.Footer>
    </Modal>
  )
}
